import {
  Box,
  Grid,
  Button,
  Typography,
  FormControl,
  OutlinedInput,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  ListItem,
  List,
  Tooltip,
} from "@mui/material";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import CuvoInputLabel from "../common/CuvoInputLabel";
import "./styles.css";
import { Http, appLogger } from "../../utils";
import { UserContext } from "../../context/userContext";
import { UserLoginInfo } from "../../types/context";
import { Intent } from "../../model/intent";
import { withSnackbar } from "../../utils/common/SnackbarHoc";

const Intents = ({ snackbarShowMessage }: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [intentList, setIntentList] = useState<Intent[]>([]);
  const [formValues, setFormValues] = useState<Intent>({
    id: "",
    name: "",
    description: "",
  });

  const { userInfo } = useContext(UserContext) as UserLoginInfo;
  const orgId = userInfo?.userDetails?.orgId || "";

  const getIntents = () => {
    if (!orgId || isLoading) return;

    setIsLoading(true);
    Http.get({
      url: `/api/v2/intents/${orgId}`,
      state: userInfo,
    })
      .then((response: any) => {
        setIntentList(response.intents);
      })
      .catch((error) => {
        appLogger.error({
          errObj: {
            method: "getIntents",
            component: "intents",
            fileName: "index.tsx",
            err: error,
          },
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((fv) => ({ ...fv, [name]: value }));
  };

  const createIntent = async () => {
    try {
      setIsLoading(true);
      await Http.post({
        url: `/api/v2/intents`,
        state: userInfo,
        body: {
          intent: {
            ...formValues,
            orgId,
          },
        },
      });
      getIntents();
    } catch (error) {
      appLogger.error({
        errObj: {
          method: "createIntent",
          component: "intents",
          fileName: "index.tsx",
          err: error,
        },
      });
    }
  };

  const updateIntent = async () => {
    try {
      setIsLoading(true);
      await Http.put({
        url: `/api/v2/intents`,
        state: userInfo,
        body: {
          intent: {
            ...formValues,
            orgId,
          },
        },
      });
      getIntents();
    } catch (error) {
      appLogger.error({
        errObj: {
          method: "updateIntent",
          component: "intents",
          fileName: "index.tsx",
          err: error,
        },
      });
    }
  };

  const handleEditIntent = async (intent: Intent) => {
    setFormValues({
      ...intent,
    });
  };

  const handleDeleteIntent = async (id: string) => {
    if (!id) return;

    try {
      setIsLoading(true);
      await Http.deleteReq({
        url: `/api/v2/intents/${id}`,
        state: userInfo,
      });
      getIntents();
    } catch (error) {
      appLogger.error({
        errObj: {
          method: "handleDeleteIntent",
          component: "intents",
          fileName: "index.tsx",
          err: error,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormValues({
      id: "",
      name: "",
      description: "",
    });
  };

  const handleCreateOrUpdateIntent = async () => {
    if (!orgId) {
      return snackbarShowMessage("Missing Organization ID", "error");
    }
    if (!formValues.name) {
      return snackbarShowMessage("Name is required", "error");
    }

    if (formValues.id) {
      await updateIntent();
      handleReset();
      return;
    }

    await createIntent();
    handleReset();
  };

  useEffect(() => {
    getIntents();
  }, [orgId]);

  return (
    <Box>
      <Box className="intent-form">
        <Grid
          container
          item
          xs={12}
          sm={8}
          md={6}
          sx={{
            border: "1px solid #ced3da",
            borderRadius: "8px",
            mb: "30px",
          }}
        >
          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            sx={{
              padding: "8px 20px",
              borderBottom: "1px solid #ced3da",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: "#092146",
              }}
            >
              {formValues.id ? "Update Intent" : "Create Intent"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                onClick={handleReset}
                disabled={isLoading}
                sx={{
                  fontWeight: 600,
                  border: "1px solid #ced3da",
                  borderRadius: "100px!important",
                }}
                variant="outlined"
                color="warning"
              >
                Reset
              </Button>
              <Button
                onClick={handleCreateOrUpdateIntent}
                disabled={isLoading}
                sx={{
                  fontWeight: 600,
                  color: "#2858D3",
                  border: "1px solid #ced3da",
                  borderRadius: "100px!important",
                }}
                variant="outlined"
              >
                {formValues.id ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>

          <Grid
            container
            gap={2.5}
            sx={{
              padding: "16px 20px",
              width: "100%",
              flexDirection: "column",
              marginBottom: "4px",
            }}
          >
            <FormControl className="custom-select" fullWidth>
              <CuvoInputLabel label="Name" showRequiredIndicator />
              <OutlinedInput
                type="text"
                name="name"
                value={formValues["name"]}
                onChange={handleTextChange}
              />
            </FormControl>
            <FormControl className="custom-select" fullWidth>
              <CuvoInputLabel label="Description" />
              <OutlinedInput
                type="text"
                name="description"
                value={formValues["description"]}
                onChange={handleTextChange}
              />
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{
              color: "red",
              justifyContent: "end",
              fontSize: "10px",
              fontWeight: "400",
              paddingRight: 3,
              paddingBottom: 1,
              marginTop: -1,
            }}
          >
            * indicates required field
          </Grid>
        </Grid>
      </Box>

      <Box className={isLoading ? "table loader" : "table"}>
        {intentList.length ? (
          <Box className="products-table table-wrap">
            <Table sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Description</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Action</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {intentList.map((intent) => {
                  return (
                    <TableRow key={intent.id}>
                      <TableCell>{intent.name}</TableCell>
                      <TableCell>{intent.description}</TableCell>
                      <TableCell>
                        <List
                          className="table-options"
                          sx={{ display: "flex" }}
                        >
                          <Tooltip title="Edit Intent" placement="top">
                            <ListItem onClick={() => handleEditIntent(intent)}>
                              <i className="icon-edit" />
                            </ListItem>
                          </Tooltip>

                          <Tooltip title="Delete Intent" placement="top">
                            <ListItem
                              onClick={() =>
                                handleDeleteIntent(intent.id || "")
                              }
                            >
                              <i className="icon-delete" />
                            </ListItem>
                          </Tooltip>
                        </List>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box
            className="products-table table-wrap"
            sx={{
              padding: 2,
              fontSize: "13px",
              backgroundColor: "rgba(0, 0, 0, 0.03)",
            }}
          >
            No intents found!
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default withSnackbar(Intents);
